import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { RxPerson } from 'react-icons/rx';
import LoginOptions from 'src/components/app/auth/components/@shared/login-options';
import * as Yup from 'yup';

import cn from '@/lib/clsxm';

import Container from '@/components/@base/container';
import { Input } from '@/components/@base/input';
import Loading from '@/components/@base/loading';
import AuthForm from '@/components/app/auth/components/@shared/auth-form';
import { LOGIN_BY } from '@/components/app/auth/components/@shared/login-options/type';
import RecoveryLink from '@/components/app/auth/components/@shared/recovery-link';
import CountryCode from '@/components/app/auth/components/login/country-code';
import Terms from '@/components/app/auth/components/login/terms';
import Props from '@/components/app/auth/components/login/type';
import { useGetRecaptchaStatus } from '@/components/app/auth/services/hooks';
import Captcha from '@/components/shared/components/recaptcha';

import { STEP, useAuthStepStore } from '@/store/auth.store';

import { DATA_CY_LOGIN } from '@/constant/cypress';
import ICON_SIZE, { ICON_COLOR } from '@/constant/icon-size-color';
import { pageLevelLocalization } from '@/constant/localization';
type CaptchaRefType = {
  reset: () => void;
};
const { login: loginLocalization } = pageLevelLocalization;

export default function Login({ onSubmit, isLoading }: Props) {
  const captchaRef = useRef<CaptchaRefType | null>(null);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const {
    phoneNumber,
    setPhoneNumber,
    setUsername,
    shouldEnterUsername,
    recoveryMode,
    username,
    enterByPassword,
    step,
    setStep,
  } = useAuthStepStore();
  const { data: recaptchaData } = useGetRecaptchaStatus();
  const handleInputChange = (e, setFieldValue) => {
    const { value } = e.target;
    if (/^(?:(?:\+98|0098|0)9\d{0,9})$/.test(value)) {
      setPhoneNumber(value);
      setUsername('');
      setFieldValue('username', value);
    } else {
      setUsername(value);
      setPhoneNumber('');
      setFieldValue('username', value);
    }
  };

  useEffect(() => {
    if ([STEP.AUTH_STEP_GET_MOBILE, STEP.AUTH_STEP_GET_EMAIL].includes(step)) {
      setPhoneNumber('');
      setUsername('');
    }
  }, [step]);

  useEffect(() => {
    if (!recaptchaData?.status) {
      setIsRecaptchaReady(false);
      return;
    }

    setIsRecaptchaReady(false);

    let cleanedUp = false;
    let delayTimeoutId: NodeJS.Timeout | null = null;

    const waitForRecaptchaContent = () => {
      let attempts = 0;
      const maxAttempts = 150; // 150 * 200ms = 30 seconds max

      let foundProperSize = false;
      let foundProperSizeAttempt = 0;

      const checkContent = () => {
        if (cleanedUp) {
          return;
        }

        attempts++;

        // Check if recaptcha is fully rendered and visible
        const recaptchaDiv = document.querySelector('#recaptcha');
        if (recaptchaDiv) {
          const iframe = recaptchaDiv.querySelector('iframe');

          if (iframe) {
            try {
              // Try to access iframe content (may fail due to cross-origin)
              const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

              // Check for specific recaptcha elements inside iframe
              const hasCheckbox = iframeDoc?.querySelector('.recaptcha-checkbox');
              const hasCheckboxBorder = iframeDoc?.querySelector('.recaptcha-checkbox-border');
              const hasAnchor = iframeDoc?.querySelector('.rc-anchor');

              // Only consider ready if we have the actual checkbox UI elements
              if ((hasCheckbox && hasCheckboxBorder) || hasAnchor) {
                delayTimeoutId = setTimeout(() => {
                  if (!cleanedUp) setIsRecaptchaReady(true);
                }, 1000);
                return;
              }
            } catch (e) {
              // Cross-origin - can't access iframe content
              // Check if iframe has proper dimensions
              const hasProperSize = iframe.offsetWidth > 250 && iframe.offsetHeight > 60;

              if (hasProperSize) {
                if (!foundProperSize) {
                  foundProperSize = true;
                  foundProperSizeAttempt = attempts;
                } else {
                  const waitedAttempts = attempts - foundProperSizeAttempt;

                  // After 12 seconds of having proper size, assume it's ready
                  if (waitedAttempts >= 60) {
                    // 60 * 200ms = 12 seconds
                    delayTimeoutId = setTimeout(() => {
                      if (!cleanedUp) setIsRecaptchaReady(true);
                    }, 1000);
                    return;
                  }
                }
              }
            }
          }
        }

        if (attempts < maxAttempts) {
          delayTimeoutId = setTimeout(checkContent, 200);
        } else {
          if (!cleanedUp) setIsRecaptchaReady(true);
        }
      };

      checkContent();
    };

    const checkIframeReady = () => {
      const recaptchaDiv = document.querySelector('#recaptcha');

      if (recaptchaDiv) {
        // Check if iframe inside the div exists and is loaded
        const iframe = recaptchaDiv.querySelector('iframe');

        if (iframe) {
          // If iframe is already loaded
          if (iframe.contentWindow) {
            try {
              // Check if iframe content is accessible (loaded)
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              if (iframeDoc && iframeDoc.readyState === 'complete') {
                return true;
              }
            } catch (e) {
              // Cross-origin, but iframe exists - consider it ready
              return true;
            }
          }

          // Wait for iframe to load
          iframe.onload = () => {
            if (!cleanedUp) {
              waitForRecaptchaContent();
            }
          };

          return 'pending'; // Special return value
        }
      }

      return false;
    };

    const initialCheck = checkIframeReady();
    if (initialCheck === true) {
      waitForRecaptchaContent();
      return () => {
        cleanedUp = true;
        if (delayTimeoutId) clearTimeout(delayTimeoutId);
      };
    }

    // If iframe is pending (waiting for onload), just set timeout as fallback
    if (initialCheck === 'pending') {
      const timeout = setTimeout(() => {
        if (!cleanedUp) {
          setIsRecaptchaReady(true);
        }
      }, 30000);

      return () => {
        cleanedUp = true;
        if (delayTimeoutId) clearTimeout(delayTimeoutId);
        clearTimeout(timeout);
      };
    }

    // If not found yet, observe DOM for changes
    const observer = new MutationObserver(() => {
      if (cleanedUp) return;

      const result = checkIframeReady();
      if (result === true) {
        waitForRecaptchaContent();
        observer.disconnect();
      } else if (result === 'pending') {
        // Iframe found, onload listener set, stop observing
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const timeout = setTimeout(() => {
      if (!cleanedUp) {
        setIsRecaptchaReady(true);
        observer.disconnect();
      }
    }, 30000);

    return () => {
      cleanedUp = true;
      if (delayTimeoutId) clearTimeout(delayTimeoutId);
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [recaptchaData?.status]);

  const formContext = {
    title: getTitle(),
    placeholder: getPlaceholder(),
    submitText: getButtonTitle(),
    backLink: backHandler,
  };

  function getTitle() {
    switch (step) {
      case STEP.AUTH_STEP_GET_MOBILE:
        return loginLocalization.enterPhone;
      case STEP.AUTH_STEP_GET_EMAIL:
        return loginLocalization.enterEmail;
      default:
        return recoveryMode || shouldEnterUsername
          ? loginLocalization.enterPhoneNumberOrEmail
          : loginLocalization.loginAndRegister;
    }
  }

  function getButtonTitle() {
    if (recoveryMode) {
      return loginLocalization.sendVerificationCode;
    } else {
      return loginLocalization.loginOrRegister;
    }
  }

  function getPlaceholder() {
    switch (step) {
      case STEP.AUTH_STEP_GET_MOBILE:
        return loginLocalization.phone;
      case STEP.AUTH_STEP_GET_EMAIL:
        return loginLocalization.email;
      default:
        return loginLocalization.emailOrPhone;
    }
  }

  function backHandler() {
    if ([STEP.AUTH_STEP_GET_MOBILE, STEP.AUTH_STEP_GET_EMAIL].includes(step)) {
      setStep(STEP.AUTH_STEP_START);
    }
  }

  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
  });

  return (
    <Formik
      initialValues={{
        username: phoneNumber || username,
        recaptcha: null,
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const recaptchaToken = values.recaptcha || '';
        onSubmit(recaptchaToken, () => {
          if (captchaRef.current) {
            captchaRef.current.reset();
          }
        });
      }}
    >
      {({ values, setFieldValue }) => (
        <Form data-cy={DATA_CY_LOGIN}>
          <AuthForm
            title={formContext.title}
            submitText={formContext.submitText}
            isLoading={isLoading}
            backHandler={formContext.backLink}
            hideBackButton={step === STEP.AUTH_STEP_START}
            actions={
              <Container center className='items-end justify-between pt-5'>
                <Terms />
                {!shouldEnterUsername &&
                  (username ? (
                    <RecoveryLink />
                  ) : (
                    <LoginOptions
                      recaptchaToken={values.recaptcha}
                      loginBy={enterByPassword ? LOGIN_BY.CODE : LOGIN_BY.PASSWORD}
                    />
                  ))}
              </Container>
            }
          >
            <Input
              id='username'
              className='group border-0 pr-3 text-[15px] text-[#76767C] placeholder-[#76767C] placeholder:text-[15px] placeholder:font-medium'
              containerClassName='h-[60px] relative shadow-[0px_2px_8px_0px_rgba(73,75,84,0.05)] p-4 border border-[#E2E2E2] w-full focus-within:border-[#FFC87B] focus-within:shadow-[0px_2px_6px_0px_rgba(255,150,6,0.19)]'
              placeholder={formContext.placeholder}
              onChange={(e) => handleInputChange(e, setFieldValue)}
              value={phoneNumber || username}
              Icon={() => (phoneNumber ? <CountryCode /> : null)}
              PlaceholderIcon={() => <RxPerson color={ICON_COLOR.gray} size={ICON_SIZE.md} />}
            />
            {recaptchaData?.status && (
              <div className='relative mt-4 flex h-[74px] w-full'>
                {/* Loading placeholder - absolute positioned */}
                <div
                  className={cn(
                    'absolute top-0 right-[7%] transition-opacity duration-300 ease-out',
                    isRecaptchaReady
                      ? 'pointer-events-none opacity-0'
                      : 'pointer-events-auto z-10 opacity-100',
                  )}
                >
                  <Container className='flex h-[74px] w-[300px] items-center gap-2 rounded-sm border border-[#d3d3d3] bg-[#f9f9f9] pr-3'>
                    <Loading className='h-20 w-8' />
                    <div className='font-serif text-sm font-[400] text-black'>
                      {loginLocalization.IAmNotRobot}
                    </div>
                  </Container>
                </div>

                {/* Recaptcha - absolute positioned */}
                <div
                  className={cn(
                    'absolute top-0 right-[7%]',
                    isRecaptchaReady
                      ? 'pointer-events-auto z-10 opacity-100'
                      : 'pointer-events-none opacity-0',
                  )}
                >
                  <Captcha
                    id='recaptcha'
                    onChange={setFieldValue}
                    ref={captchaRef}
                    className='!mt-0'
                  />
                </div>
              </div>
            )}
          </AuthForm>
        </Form>
      )}
    </Formik>
  );
}
